import {
	FormProvider,
	getFormProps,
	getInputProps,
	getTextareaProps,
	useForm,
} from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import { type Recibo, type Constancia } from '@prisma/client'
import { type ActionFunctionArgs } from '@remix-run/node'
import { Form, json, useActionData } from '@remix-run/react'
import { z } from 'zod'
import {
	Field,
	TextareaField,
	ErrorList,
	UsoDeCFDIField,
	FormaDePagoField,
} from '#app/components/forms.tsx'
import { StatusButton } from '#app/components/ui/status-button.tsx'
import { requireUserId } from '#app/utils/auth.server.ts'
import { prisma } from '#app/utils/db.server.ts'
import { useIsPending } from '#app/utils/misc.tsx'
import { CostcoFormSchema } from '#app/utils/schemas.ts'
import { redirectWithToast } from '#app/utils/toast.server.ts'
import { runCostco } from '#app/vendors/costco.ts'

export async function action({ request }: ActionFunctionArgs) {
	const userId = await requireUserId(request)
	const formData = await request.formData()
	const owner = await prisma.user.findFirst({
		select: {
			id: true,
			name: true,
			username: true,
			image: { select: { id: true } },
			notes: { select: { id: true, title: true } },
		},
		where: { id: userId },
	})

	const submission = await parseWithZod(formData, {
		schema: CostcoFormSchema.superRefine(async (data, ctx) => {
			const { num_ticket: numTicket, uso_de_cfdi, monto_total } = data

			if (!numTicket) {
				ctx.addIssue({
					path: ['num_ticket'],
					code: z.ZodIssueCode.custom,
					message: 'El número de ticket es requerido',
				})
			}
			if (!uso_de_cfdi) {
				ctx.addIssue({
					path: ['uso_de_cfdi'],
					code: z.ZodIssueCode.custom,
					message: 'Uso de CFDI es requerido',
				})
			}
			if (!monto_total) {
				ctx.addIssue({
					path: ['monto_total'],
					code: z.ZodIssueCode.custom,
					message: 'El monto es requerido',
				})
			}
		}),
		async: true,
	})

	if (submission.status !== 'success') {
		return json(
			{ result: submission.reply() },
			{ status: submission.status === 'error' ? 400 : 200 },
		)
	}

	const {
		num_ticket,
		fecha_de_compra,
		uso_de_cfdi,
		forma_de_pago,
		monto_total,
		observaciones,
	} = submission.value

	try {
		let reciboData = {} as (Recibo & { constancia: Constancia }) | null
		reciboData = await prisma.recibo.create({
			data: {
				vendor: 'costco',
				monto_total: monto_total,
				fecha_de_compra: fecha_de_compra,
				observaciones,
				uso_de_cfdi: uso_de_cfdi,
				forma_de_pago: forma_de_pago,
				numero_de_ticket: num_ticket,
				vendorData: JSON.stringify({ num_ticket: num_ticket }),
				constancia: {
					connect: {
						userId: owner?.id,
					},
				},
			},
			include: { constancia: true },
		})

		await runCostco({
			recibo: reciboData,
		})
	} catch (error) {
		json({ result: submission.reply() }, { status: 500 })
	}

	return redirectWithToast(`/recibos`, {
		type: 'success',
		title: 'Success',
		description: 'Has creado tu nuevo recibo.',
	})
}

export default function Test() {
	const actionData = useActionData<typeof action>()
	const isPending = useIsPending()

	const [form, fields] = useForm({
		id: 'costco-form',
		lastResult: actionData?.result,
		constraint: getZodConstraint(CostcoFormSchema),
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: CostcoFormSchema })
		},
	})
	return (
		<FormProvider context={form.context}>
			<Form
				method="POST"
				className="flex h-full w-full flex-col gap-y-4 overflow-y-auto overflow-x-hidden px-10 pb-4 pt-12"
				{...getFormProps(form)}
				encType="multipart/form-data"
			>
				{/*
    This hidden submit button is here to ensure that when the user hits
    "enter" on an input field, the primary form function is submitted
    rather than the first button in the form (which is delete/add image).
*/}
				<button type="submit" className="hidden" />
				<div className="flex flex-col gap-1">
					<Field
						labelProps={{ children: 'Número de ticket' }}
						inputProps={{
							...getInputProps(fields.num_ticket, { type: 'text' }),
						}}
						errors={fields.num_ticket.errors}
					/>
					<FormaDePagoField field={fields.forma_de_pago} />
					<UsoDeCFDIField field={fields.uso_de_cfdi} />
					<Field
						labelProps={{ children: 'Monto' }}
						inputProps={{
							...getInputProps(fields.monto_total, {
								type: 'number',
							}),
						}}
						errors={fields.monto_total.errors}
					/>
					<Field
						labelProps={{ children: 'Fecha de transacción' }}
						inputProps={{
							...getInputProps(fields.fecha_de_compra, {
								type: 'date',
							}),
						}}
						errors={fields.fecha_de_compra.errors}
					/>

					<TextareaField
						labelProps={{ children: 'Observaciones' }}
						textareaProps={{
							...getTextareaProps(fields.observaciones),
						}}
						errors={fields.observaciones.errors}
					/>
				</div>

				<ErrorList id={form.errorId} errors={form.errors} />
			</Form>
			<div className="bottom-0 left-3 right-3 flex w-full items-center justify-end gap-2 rounded-lg bg-muted/80 p-4 pl-5 shadow-xl shadow-accent backdrop-blur-sm md:gap-4 md:pl-7">
				<StatusButton
					form={form.id}
					type="submit"
					disabled={isPending}
					status={isPending ? 'pending' : 'idle'}
				>
					Enviar
				</StatusButton>
			</div>
		</FormProvider>
	)
}
